import { CompanyType, IssueType, OrderIssueResolutionType } from '@planning/app/api'
import { FieldboxWithRadio } from '@planning/components/atoms/FieldboxWithRadio'
import { CompanyAutocomplete } from '@planning/components/autocompletes/company-autocomplete/CompanyAutocomplete'
import { FieldboxSelectionGroup } from '@planning/components/molecules/FieldboxSelectionGroup'
import { RegisteredChangeTransportSubForm } from '@planning/components/organisms/ChangeTransportSubForm'
import { CustomsSubForm } from '@planning/components/organisms/CustomsSubForm'
import { StowagePositionSubForm } from '@planning/components/stowage-position/organisms/StowagePositionSubForm'
import { useTranslate } from '@tolgee/react'
import { FieldStore, FormField, IFormStore, required, useRegisterSubFormStore } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { IssueResolutionViewStore } from '../../Stores/IssueResolutionViewStore'
import { UpdateCompanyFieldbox } from '../molecules/UpdateCompanyFieldbox'
import { ResolveIssuesFormData } from '../templates/ResolveIssueDialog'

interface IProps {
  store: IssueResolutionViewStore
  formStore: IFormStore<ResolveIssuesFormData>
}

const customsIssueTypes = new Set<IssueType>([
  IssueType.CustomsPending,
  IssueType.CustomsRefused,
  IssueType.CustomsInspectionRequired,
])

export const ResolveIssuesFormList = observer(({ store, formStore }: IProps) => {
  const { t } = useTranslate()
  const selectedIssueResolutionStore = useRegisterSubFormStore(
    formStore,
    'selectedIssueResolution',
    new FieldStore<string>(OrderIssueResolutionType.FollowUp, required()),
  )

  if (!store.issueToBeResolved) return <></>

  const issueType = store.issueToBeResolved?.data.issueType

  return (
    <FieldboxSelectionGroup
      onSelect={selectedIssueResolutionStore.setValue}
      defaultValue={OrderIssueResolutionType.FollowUp}
      options={controller => (
        <>
          {(issueType === IssueType.MissingRailcar || issueType === IssueType.Overlanded) && (
            <FieldboxWithRadio
              selectionKey={OrderIssueResolutionType.ChangeOutboundTransport}
              controller={controller}
              label={
                issueType === IssueType.Overlanded
                  ? t('assignOutboundTransport', 'Assign outbound transport')
                  : t('changeOutboundTransport', 'Change outbound transport')
              }
            >
              <RegisteredChangeTransportSubForm
                formStore={formStore}
                name={'changeTransport'}
                hideUniversalOption
              />
            </FieldboxWithRadio>
          )}
          {issueType === IssueType.Shortlanded && (
            <FieldboxWithRadio
              selectionKey={OrderIssueResolutionType.ChangeInboundTransport}
              controller={controller}
              label={t('changeInbountTranport', 'Change inbound transport')}
            >
              <RegisteredChangeTransportSubForm
                formStore={formStore}
                name={'changeTransport'}
                hideUniversalOption
              />
            </FieldboxWithRadio>
          )}
          {issueType === IssueType.MissingRailcar ||
            (issueType === IssueType.Shortlanded && (
              <FieldboxWithRadio
                selectionKey={OrderIssueResolutionType.DeleteOrder}
                controller={controller}
                label={t('removeOrder', 'Remove order')}
              />
            ))}
          {issueType === IssueType.MissingRailcar && (
            <FieldboxWithRadio
              selectionKey={OrderIssueResolutionType.RailcarFound}
              controller={controller}
              label={t('railcarWasFound', 'Railcar was found')}
            />
          )}
          {issueType === IssueType.Shortlanded && (
            <FieldboxWithRadio
              selectionKey={OrderIssueResolutionType.ContainerFound}
              controller={controller}
              label={t('containerFound', 'Container found on original railcar')}
            />
          )}
          {customsIssueTypes.has(issueType) && (
            <FieldboxWithRadio
              selectionKey={OrderIssueResolutionType.UpdateCustomsStatus}
              controller={controller}
              label={t('updateCustomsStatus', 'Update customs status’')}
            >
              <CustomsSubForm
                formStore={formStore}
                name={'customs'}
                initialValue={
                  store.issueToBeResolved?.containerJourney?.outboundOrder?.data?.customs ??
                  undefined
                }
              />
            </FieldboxWithRadio>
          )}

          {(issueType === IssueType.MissingCompany || issueType === IssueType.UnknownCompany) && (
            <UpdateCompanyFieldbox
              controller={controller}
              formStore={formStore}
              issueResolutionStore={store}
            />
          )}

          {/* Deprecated. Use UnknownCompany instead: */}
          {issueType === IssueType.MissingConsignee && (
            <FieldboxWithRadio
              selectionKey={OrderIssueResolutionType.UpdateConsignee}
              controller={controller}
              label={t('updateConsignee', 'Update consignee')}
            >
              <FormField formStore={formStore} name={'consignee'} initialValue={''}>
                {store => {
                  return (
                    <CompanyAutocomplete
                      label={t('consignee', 'Consignee')}
                      filterByTypes={[CompanyType.Consignee]}
                      selectedCompanyShortName={store.data}
                      handleOnChange={consignee => store.setValue(consignee?.shortName ?? '')}
                    />
                  )
                }}
              </FormField>
            </FieldboxWithRadio>
          )}

          {/* Deprecated. Use UnknownCompany instead: */}
          {issueType === IssueType.MissingOperator && (
            <FieldboxWithRadio
              selectionKey={OrderIssueResolutionType.UpdateOperator}
              controller={controller}
              label={t('updateOperator', 'Update operator')}
            >
              <FormField formStore={formStore} name={'operator'} initialValue={''}>
                {store => {
                  return (
                    <CompanyAutocomplete
                      label={t('operator', 'Operator')}
                      filterByTypes={[CompanyType.ShippingLine]}
                      selectedCompanyShortName={store.data}
                      handleOnChange={operator => store.setValue(operator?.shortName ?? '')}
                    />
                  )
                }}
              </FormField>
            </FieldboxWithRadio>
          )}
          {issueType === IssueType.Overlanded && (
            <FieldboxWithRadio
              selectionKey={OrderIssueResolutionType.ChangeToRestow}
              controller={controller}
              label={t('markAsRestow', 'Mark as restow')}
            >
              <StowagePositionSubForm formStore={formStore} name='stowagePosition' />
            </FieldboxWithRadio>
          )}
          {issueType === IssueType.Overlanded && (
            <FieldboxWithRadio
              selectionKey={OrderIssueResolutionType.ChangeToImport}
              controller={controller}
              label={t('markAsImport', 'Mark as import')}
            />
          )}
          {issueType !== IssueType.MissingUnNumberOfUnitLabel && (
            <FieldboxWithRadio
              selectionKey={OrderIssueResolutionType.FollowUp}
              controller={controller}
              label={t('markForFollowUp', 'Mark for follow up')}
            />
          )}
        </>
      )}
    />
  )
})
